import {
  Card,
  Checkbox,
  ExpandableText,
  TaskStatusIcon,
  Text,
} from "@/components/ui";
import type { PriorityLevel } from "@/components/ui/Icons/PriorityIcon";
import { PriorityIcon } from "@/components/ui/Icons/PriorityIcon";
import {
  MatterBreadcrumb,
  type MatterBreadcrumbProps,
} from "@/components/ui/MatterBreadcrumb.tsx/MatterBreadcrumb";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useUser } from "@/components/UserProvider";
import { useGetSubitemsForBreadcrumbs } from "@/hooks/use-get-subitems-for-breadcrumbs";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { getUsersOptions } from "@/lib/data/react-query";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  Matter,
  MatterSubItemType,
  MatterUserConnection,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDate, formatUsersName } from "@/lib/utils/format";
import dayjs from "dayjs";
import { kebabCase, toLower } from "lodash";
import { Bell, Bot, CalendarClock, CircleUser } from "lucide-react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { MatterTaskContextMenu } from "./MatterTaskContextMenu";
import type { MatterTaskWithType } from "./matter-task-columns";

const isOverdue = (task: MatterTaskWithType) => {
  const hasValidDue = !!task.dueDate && dayjs(task.dueDate).isValid();
  if (!hasValidDue || task.status === "Completed") return false;
  const due = dayjs(task.dueDate as string, "YYYY-MM-DD");
  return due.isBefore(dayjs());
};

const taskSelectionCheckboxClassName =
  "relative z-10 mt-1 border-slate-300 opacity-0 transition-[opacity,border-color,background-color] after:absolute after:-inset-x-3 after:-inset-y-[13px] after:content-[''] group-hover:opacity-100 hover:border-blue-500 data-[state=checked]:border-blue-500 data-[state=checked]:bg-blue-500 data-[state=checked]:text-white";

// These are here to avoid rewriting the same code for multiple variants
const TaskPriorityIcon = ({ priority }: { priority: PriorityLevel | null }) => {
  return (
    <div className="border-muted-foreground/20 rounded-md border p-1">
      <PriorityIcon
        level={priority ? (toLower(priority) as PriorityLevel) : "none"}
      />
    </div>
  );
};

const TaskAssigneeIcon = ({
  assignees,
  usersById,
}: {
  assignees: MatterUserConnection[];
  usersById: Map<
    string,
    { firstName?: string | null; id?: string; lastName?: string | null }
  >;
}) => {
  const assignedUsers = assignees
    .map((assignee) =>
      assignee.userId ? usersById.get(assignee.userId) : undefined,
    )
    .filter(
      (
        user,
      ): user is {
        firstName?: string | null;
        id?: string;
        lastName?: string | null;
      } => !!user?.id,
    );
  const appAssignees = assignees.filter(
    (assignee) => assignee.integrationAppId,
  );
  const visibleUsers = assignedUsers.slice(0, 3);
  const remainingCount = assignedUsers.length - visibleUsers.length;

  return (
    <div className="border-muted-foreground/20 flex h-7 items-center rounded-md border p-1">
      {appAssignees.map((app) => (
        <TooltipWrapper
          key={app.integrationAppId}
          message={`${app.integrationAppName ?? "Unavailable app"} (app)`}
        >
          <Bot
            className="text-primary h-4 w-4"
            aria-label={`${app.integrationAppName ?? "Unavailable app"} (app)`}
          />
        </TooltipWrapper>
      ))}
      {visibleUsers.length ? (
        <div className="isolate flex items-center -space-x-1.5">
          {visibleUsers.map((user, index) => (
            <TooltipWrapper key={user.id} message={formatUsersName(user)}>
              <div
                className="ring-background relative rounded-full ring-2"
                style={{ zIndex: visibleUsers.length - index }}
              >
                <UserProfilePhoto
                  user={{
                    id: user.id ?? "",
                    firstName: user.firstName ?? "",
                    lastName: user.lastName ?? "",
                  }}
                  className="h-4 w-4"
                  initialsClassName="text-[8px]"
                />
              </div>
            </TooltipWrapper>
          ))}
          {remainingCount > 0 && (
            <span className="bg-muted text-muted-foreground ring-background flex h-4 w-4 items-center justify-center rounded-full text-[8px] ring-1">
              +{remainingCount}
            </span>
          )}
        </div>
      ) : appAssignees.length ? null : (
        <CircleUser className="text-muted-foreground h-4 w-4" />
      )}
    </div>
  );
};

const TaskNotifyIcon = ({ isNotifying }: { isNotifying: boolean }) => {
  if (!isNotifying) {
    return null;
  }

  return (
    <TooltipWrapper message="Notifications on">
      <div className="border-muted-foreground/20 rounded-md border p-1">
        <Bell className="text-primary h-4 w-4" />
      </div>
    </TooltipWrapper>
  );
};

const TaskDueDateIcon = ({ task }: { task: MatterTaskWithType }) => {
  const dueDateIsCurrentYear = dayjs(task.dueDate).isSame(dayjs(), "year");
  const overdue = isOverdue(task);
  return (
    <div className="border-muted-foreground/20 rounded-md border p-1">
      {task.dueDate ? (
        <span className="flex items-center gap-x-1">
          <CalendarClock
            className={cn("h-4 min-w-4", overdue && "text-destructive")}
          />
          <Text
            variant="caption"
            color={overdue ? "destructive" : "muted"}
            as="span"
          >
            {formatDate(task.dueDate, !dueDateIsCurrentYear)}
          </Text>
        </span>
      ) : (
        <span className="flex items-center gap-x-1">
          <CalendarClock className="h-4 min-w-4" />
          <Text variant="caption" color="muted" as="span">
            Not set
          </Text>
        </span>
      )}
    </div>
  );
};

export default function MatterTask({
  task,
  matter,
  massTortId,
  subItem,
  variant = "card",
  isSelected = false,
  onToggleSelect,
  selectedTasks = [],
  customBreadcrumb,
}: {
  task: MatterTaskWithType;
  matter: Pick<Matter, "id"> & Partial<Pick<Matter, "assignees">>;
  massTortId?: string;
  subItem?: { id: string; type: MatterSubItemType };
  variant?: "card" | "eventlog" | "list";
  isSelected?: boolean;
  onToggleSelect?: (taskId: string) => void;
  selectedTasks?: MatterTaskWithType[];
  customBreadcrumb?: MatterBreadcrumbProps["items"];
}) {
  const { hasActionPermission, currentUser } = useUser();
  const hasUpdatePermission = hasActionPermission(
    massTortId
      ? ACTION_PERMISSION_KEYS.massTort
      : ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const { data: usersData } = useQuery(getUsersOptions());
  const usersById = useMemo(
    () => new Map((usersData?.data ?? []).map((user) => [user.id, user])),
    [usersData?.data],
  );
  const { openTaskModal } = useGlobalModal();
  const getSubitemsForBreadcrumbs = useGetSubitemsForBreadcrumbs();
  const taskMatterId = task.matterId || matter.id;
  const assignees = task.assignees ?? [];

  const breadcrumbItems = [
    {
      id: "matter",
      type: "Matter",
    },
  ] as MatterBreadcrumbProps["items"];

  if (task.type !== "Matter") {
    breadcrumbItems.push(...getSubitemsForBreadcrumbs(task));
  }
  const selectionEnabled = typeof onToggleSelect === "function";
  const handleToggleSelection = () => {
    if (!selectionEnabled) {
      return;
    }
    onToggleSelect(task.id);
  };
  const isNotified = Boolean(
    task.notifiers?.some((notifier) => notifier.userId === currentUser.id),
  );
  const tasksToEdit = isSelected ? selectedTasks : [task];
  const handleEdit = () => {
    if (!hasUpdatePermission) {
      return;
    }
    openTaskModal({
      matterId: taskMatterId,
      massTortId,
      taskId: task.id,
      editing: true,
      initialTab: "details",
      initialData: task,
      subItem,
      hideMatterField: true,
    });
  };

  // Event log–style compact variant
  if (variant === "eventlog") {
    const displayText = task.title;

    return (
      <MatterTaskContextMenu
        task={task}
        matterAssignees={matter.assignees}
        matterId={taskMatterId}
        massTortId={massTortId}
        onEdit={handleEdit}
        tasksToEdit={tasksToEdit}
        trigger={
          <Card
            className={cn(
              "focus-visible:ring-primary/30 group flex h-fit cursor-pointer items-start justify-between gap-4 overflow-visible border border-transparent p-4 transition-colors focus-visible:ring-2 focus-visible:outline-none",
              isSelected
                ? "bg-blue-100 hover:bg-blue-200 dark:bg-blue-950/60 dark:hover:bg-blue-900/70"
                : "hover:bg-neutral-200 dark:hover:bg-neutral-700",
            )}
            onClick={handleEdit}
            role={selectionEnabled ? "button" : undefined}
            aria-pressed={selectionEnabled ? !!isSelected : undefined}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between">
                <MatterBreadcrumb items={customBreadcrumb || breadcrumbItems} />
              </div>
              {displayText && (
                <TooltipWrapper message={displayText}>
                  <Text
                    variant="subHeading"
                    className="mt-1 flex items-center gap-x-2 whitespace-pre-wrap"
                  >
                    <TaskStatusIcon
                      status={
                        kebabCase(task.status) as
                          | "open"
                          | "in-progress"
                          | "completed"
                      }
                    />
                    <span className="line-clamp-2">{displayText}</span>
                  </Text>
                </TooltipWrapper>
              )}
              <div className="mt-2 flex gap-x-1">
                <TaskNotifyIcon isNotifying={isNotified} />
                <TaskDueDateIcon task={task} />
                <TaskPriorityIcon priority={task.priority as PriorityLevel} />
                <TaskAssigneeIcon assignees={assignees} usersById={usersById} />
              </div>
            </div>
          </Card>
        }
      />
    );
  }

  if (variant === "list") {
    const displayText = task.title;

    return (
      <MatterTaskContextMenu
        task={task}
        matterAssignees={matter.assignees}
        matterId={taskMatterId}
        massTortId={massTortId}
        onEdit={handleEdit}
        tasksToEdit={tasksToEdit}
        trigger={
          <Card
            className={cn(
              "focus-visible:ring-primary/30 group flex h-fit cursor-pointer items-start gap-4 overflow-visible border border-transparent p-2 transition-colors focus-visible:ring-2 focus-visible:outline-none",
              isSelected
                ? "bg-blue-100 hover:bg-blue-200 dark:bg-blue-950/60 dark:hover:bg-blue-900/70"
                : "hover:bg-neutral-200 dark:hover:bg-neutral-700",
            )}
            onClick={handleEdit}
            role={selectionEnabled ? "button" : undefined}
            aria-pressed={selectionEnabled ? !!isSelected : undefined}
          >
            <div className="min-w-0 flex-1">
              <div className="flex h-[26px] items-center gap-x-2">
                {selectionEnabled && (
                  <div
                    className="-mx-3 flex h-[42px] w-10 shrink-0 cursor-pointer items-center justify-center"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleToggleSelection();
                    }}
                  >
                    <Checkbox
                      checked={!!isSelected}
                      aria-label={isSelected ? "Deselect task" : "Select task"}
                      onCheckedChange={handleToggleSelection}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      className={cn(
                        taskSelectionCheckboxClassName,
                        isSelected && "opacity-100",
                      )}
                    />
                  </div>
                )}
                <TaskStatusIcon
                  status={
                    kebabCase(task.status) as
                      | "open"
                      | "in-progress"
                      | "completed"
                  }
                />

                <MatterBreadcrumb
                  items={customBreadcrumb || breadcrumbItems}
                  className="w-fit max-w-full shrink-0"
                />
                {displayText && (
                  <TooltipWrapper message={displayText}>
                    <Text
                      variant="body"
                      className="mt-0.5 flex min-w-0 items-center gap-x-2 leading-tight"
                    >
                      <span className="truncate">{displayText}</span>
                    </Text>
                  </TooltipWrapper>
                )}
                <div className="flex grow"></div>
                <div className="flex min-w-fit gap-x-1">
                  <TaskNotifyIcon isNotifying={isNotified} />
                  <TaskDueDateIcon task={task} />
                  <TaskPriorityIcon priority={task.priority as PriorityLevel} />
                  <TaskAssigneeIcon
                    assignees={assignees}
                    usersById={usersById}
                  />
                </div>
              </div>
            </div>
          </Card>
        }
      />
    );
  }

  return (
    <MatterTaskContextMenu
      task={task}
      matterAssignees={matter.assignees}
      matterId={taskMatterId}
      massTortId={massTortId}
      onEdit={handleEdit}
      tasksToEdit={tasksToEdit}
      trigger={
        <Card
          className={cn(
            "focus-visible:ring-primary/30 group flex h-fit cursor-pointer items-start justify-between gap-4 overflow-visible border border-transparent p-4 transition-colors focus-visible:ring-2 focus-visible:outline-none",
            isSelected
              ? "bg-blue-100 hover:bg-blue-200 dark:bg-blue-950/60 dark:hover:bg-blue-900/70"
              : "hover:bg-neutral-200 dark:hover:bg-neutral-700",
          )}
          onClick={handleEdit}
          role={selectionEnabled ? "button" : undefined}
          aria-pressed={selectionEnabled ? !!isSelected : undefined}
        >
          <div className="flex flex-col gap-1 space-y-1">
            <div className="flex items-start gap-3">
              <div>
                {selectionEnabled && (
                  <Checkbox
                    checked={!!isSelected}
                    aria-label={isSelected ? "Deselect task" : "Select task"}
                    onCheckedChange={handleToggleSelection}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    className={cn(
                      taskSelectionCheckboxClassName,
                      isSelected && "opacity-100",
                    )}
                  />
                )}
              </div>
              <div className="flex w-full flex-col gap-x-1 gap-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MatterBreadcrumb
                      items={customBreadcrumb || breadcrumbItems}
                    />
                  </div>
                </div>
                <Text variant="cardTitle" className="flex items-center gap-x-2">
                  <TaskStatusIcon
                    status={
                      kebabCase(task.status) as
                        | "open"
                        | "in-progress"
                        | "completed"
                    }
                  />
                  <span className="line-clamp-1">{task.title}</span>
                  <div className="flex grow" />
                </Text>
                {task.description && (
                  <ExpandableText
                    text={task.description || ""}
                    className="text-muted-foreground"
                    maxLines={3}
                  />
                )}
                <div className="flex gap-x-1">
                  <TaskNotifyIcon isNotifying={isNotified} />
                  <TaskDueDateIcon task={task} />
                  <TaskPriorityIcon priority={task.priority as PriorityLevel} />
                  <TaskAssigneeIcon
                    assignees={assignees}
                    usersById={usersById}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
      }
    />
  );
}
